import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../api/client'
import type { Corda, CordaSlug, PlayerProgress, Stage } from '@shared/types'
import stagesData from '../../game/data/stages.json'
import cordasData from '../../game/data/cordas.json'
import { StageCard } from '../components/StageCard'
import { getStageBackground } from '../data/stageThemes'
import SettingsModal from '../components/SettingsModal'
import LeaderboardModal from '../components/LeaderboardModal'
import PremiumOfferModal from '../components/PremiumOfferModal'
import { androidBilling } from '../utils/platform'
import { useL10n } from '../hooks/useL10n'
import './MainMenuPage.css'

type Skin = NonNullable<PlayerProgress['skin']>

const stages = (stagesData as Stage[]).filter((s) => s.scene === 'StageScene')
const stagesByChapter = stages.reduce<Record<number, Stage[]>>((acc, s) => {
  ;(acc[s.chapter] ??= []).push(s)
  return acc
}, {})
const chapters = Object.keys(stagesByChapter)
  .map(Number)
  .sort((a, b) => a - b)

const cordas = cordasData as Corda[]

interface CordaProgress {
  currentXp: number
  targetXp: number
  percent: number
  currentName: string
  nextName: string | null
  color: string
  isMax: boolean
}

function computeCordaProgress(currentCorda: CordaSlug, totalXp: number, t: (key: string) => string): CordaProgress {
  const idx = Math.max(0, cordas.findIndex((c) => c.slug === currentCorda))
  const current = cordas[idx]
  const next = cordas[idx + 1]

  if (!next) {
    return {
      currentXp: totalXp,
      targetXp: totalXp,
      percent: 1,
      currentName: t(`cordas.${current.slug}`) || current.name,
      nextName: null,
      color: current.color,
      isMax: true,
    }
  }
  const span = next.xpRequired - current.xpRequired
  const into = Math.max(0, totalXp - current.xpRequired)
  return {
    currentXp: into,
    targetXp: span,
    percent: span > 0 ? Math.min(1, into / span) : 1,
    currentName: t(`cordas.${current.slug}`) || current.name,
    nextName: t(`cordas.${next.slug}`) || next.name,
    color: current.color,
    isMax: false,
  }
}

const SPECIALS = [
  {
    id: 'special_boss_rush',
    name: 'Roda de Mestres (Boss Rush)',
    kind: 'boss' as const,
    description: 'Desafio retrô definitivo! Enfrente todas as 13 lendas consecutivamente sob a HUD clássica de Arcade.',
  },
  {
    id: 'boss_mestre_passaro',
    name: 'Desafio do Mestre Pássaro',
    kind: 'boss' as const,
    description: 'Confronto em 3 fases. HP elevado, ataques telegrafados.',
  },
  {
    id: 'boss_curupira_mata',
    name: 'Curupira da Mata',
    kind: 'boss' as const,
    description: 'Boss do Capítulo 3. HP 360, três fases, cenário do Pantanal.',
  },
  {
    id: 'special_berimbau_touch',
    name: 'Tocando o Berimbau',
    kind: 'rhythm' as const,
    description: 'Arraste o dedo na corda para gerar Axé de Cura. Essencial para recuperar vida.',
  },
  {
    id: 'rhythm_berimbau_basico',
    name: 'Treino de Ritmo',
    kind: 'rhythm' as const,
    description: 'Acerte o ritmo (Z / X / C) para treinar sua musicalidade.',
  },
  {
    id: 'survival',
    name: 'Modo Survival',
    kind: 'survival' as const,
    description: 'Enfrente ondas infinitas de inimigos e tente sobreviver o máximo possível.',
  },
  {
    id: 'training_sala',
    name: 'Sala de Treino',
    kind: 'training' as const,
    description: 'Pratique golpes e combos sem pressão. HP infinito, dummy configurável.',
  },
]

export default function MainMenuPage() {
  const { profile, progress, logout, setProgress } = useAuth()
  const navigate = useNavigate()
  const { t } = useL10n()

  // Resolve nome localizado da fase com fallback para o name PT do JSON.
  const stageName = (s: { id: string; nameKey?: string; name: string }) => {
    const key = s.nameKey ?? `stages.${s.id}`
    const translated = t(key)
    return translated === key ? s.name : translated
  }
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isRankingOpen, setIsRankingOpen] = useState(false)
  const [isPremiumOpen, setIsPremiumOpen] = useState(false)
  const isPremium = Boolean(progress?.isPremium)

  useEffect(() => {
    // Garante modo vertical no Menu
    ;(window as any).AndroidBridge?.lockPortrait?.()

    // Mostra banner AdMob no rodapé (não premium). Esconde ao desmontar
    // ou quando o jogador entrar em uma fase. Sem-op em web/PC.
    if (!isPremium) androidBilling()?.showBanner?.()
    return () => {
      androidBilling()?.hideBanner?.()
    }
  }, [isPremium])

  // Auto-load da fase zero: só para jogador novo (sem progresso) que ainda
  // não viu o tutorial neste dispositivo. A flag é gravada pelo GamePage ao
  // concluir OU abandonar (evento tutorial:done).
  useEffect(() => {
    if (!progress) return
    let done = false
    try {
      done = localStorage.getItem('cs_tutorial_done') === '1'
    } catch {
      /* ignore */
    }
    const isNewPlayer = (progress.maxStageIndex ?? 0) === 0
    if (!done && isNewPlayer) {
      navigate('/play/stage_00_tutorial')
    }
  }, [progress, navigate])

  // Auto-scroll até a fase PRÓXIMA (a primeira ainda não vencida) assim
  // que o progress hidrata. Evita o jogador ter que rolar manualmente
  // para encontrar o próximo desafio depois de muitas fases concluídas.
  const maxStageIndex = progress?.maxStageIndex
  useEffect(() => {
    if (maxStageIndex == null) return
    // setTimeout: aguarda o React montar os cards depois do early-return.
    const handle = window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>('.stage-card[data-selected="true"]')
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 120)
    return () => window.clearTimeout(handle)
  }, [maxStageIndex])

  const changeSkin = async (skin: Skin) => {
    if (!progress) return
    setProgress({ ...progress, skin })
    try {
      await api.setSkin(skin)
    } catch (err) {
      console.error('Falha ao salvar skin:', err)
    }
  }

  const changeDifficulty = async (difficulty: 'easy' | 'medium' | 'hard') => {
    if (!progress) return
    setProgress({ ...progress, difficulty })
    try {
      await api.setDifficulty(difficulty)
    } catch (err) {
      console.error('Falha ao salvar dificuldade:', err)
    }
  }

  const corda = computeCordaProgress(progress?.currentCorda ?? 'branca', progress?.totalXp ?? 0, t)
  const xpPct = Math.round(corda.percent * 100)
  const skin = progress?.skin ?? 'negro'

  // O progresso (incluindo maxStageIndex) chega async do backend depois
  // que useAuth hidrata. Renderizar antes faria todas as fases vencidas
  // aparecerem trancadas até a primeira interação — mostramos um estado
  // de carregamento para evitar esse flash.
  if (!progress) {
    return (
      <div className="menu-page menu-page--loading">
        <div className="menu-loading">{t('ui.loading')}</div>
      </div>
    )
  }

  return (
    <div className="menu-page">
      <header className="menu-header">
        <div className="menu-header__brand">
          <img
            src="/assets/logo-capoeirasoul.png"
            alt="Capoeira Soul — Arcade 2D"
            className="menu-header__logo"
            draggable={false}
          />
          <h1 className="sr-only">Capoeira Soul</h1>
          <p className="menu-header__tagline">
            {t('ui.tagline')}
          </p>
        </div>

        <div className="player-card">
          <span
            className="player-card__corda-chip"
            style={{ ['--corda-color' as string]: corda.color } as React.CSSProperties}
            aria-hidden="true"
            title={corda.currentName}
          />

          <div className="player-card__main">
            <div className="player-card__name">{profile?.username ?? '—'}</div>
            <div className="player-card__corda-label">{corda.currentName}</div>

            <div
              className="player-card__xp-bar"
              role="progressbar"
              aria-label="Progresso até a próxima corda"
              aria-valuenow={xpPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="player-card__xp-fill" style={{ width: `${xpPct}%` }} />
            </div>
            <div className="player-card__xp-meta">
              <span>{progress?.totalXp ?? 0} XP</span>
              <span>
                {corda.isMax
                  ? t('ui.mastery_reached')
                  : `${corda.currentXp} / ${corda.targetXp} ${t('ui.to')} ${corda.nextName}`}
              </span>
            </div>
            <button
              type="button"
              className="player-card__difficulty-chip"
              data-difficulty={progress?.difficulty ?? 'medium'}
              onClick={() => setIsSettingsOpen(true)}
              aria-label={t('difficulty.change')}
              title={t('difficulty.change')}
            >
              <span className="player-card__difficulty-label">{t('difficulty.label')}:</span>
              <strong>{t(`difficulty.${progress?.difficulty ?? 'medium'}`)}</strong>
            </button>
          </div>


          <button
            type="button"
            className="player-card__logout"
            onClick={logout}
            aria-label={t('ui.logout_aria')}
          >
            {t('ui.logout')}
          </button>


          <button
            type="button"
            className="player-card__settings"
            onClick={() => setIsSettingsOpen(true)}
            aria-label="Abrir configurações"
          >
            <SettingsIcon />
          </button>
        </div>

        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          healCharges={progress?.axe_charges || 0}
          currentSkin={skin}
          onChangeSkin={changeSkin}
          currentDifficulty={progress?.difficulty ?? 'medium'}
          onChangeDifficulty={changeDifficulty}
          isPremium={isPremium}
          onOpenPremium={() => setIsPremiumOpen(true)}
        />

        <PremiumOfferModal
          isOpen={isPremiumOpen}
          onClose={() => setIsPremiumOpen(false)}
          onPurchaseConfirmed={() => {
            if (progress) setProgress({ ...progress, isPremium: true })
            setIsPremiumOpen(false)
          }}
        />

        <LeaderboardModal
          isOpen={isRankingOpen}
          onClose={() => setIsRankingOpen(false)}
        />
      </header>

      <main className="menu-main">
        <div className="menu-main__inner">
          {chapters.map((ch) => (
            <section key={ch} className="menu-section">
              <h2 className="menu-section__title">{t('ui.map_chapter').replace('{ch}', ch.toString())}</h2>
              <div className="menu-section__grid">
                {stagesByChapter[ch].map((s) => {
                  const globalIndex = stages.findIndex(st => st.id === s.id)
                  const maxIdx = progress?.maxStageIndex ?? 0
                  const isLocked = globalIndex > maxIdx
                  const isNext = globalIndex === maxIdx
                  const isCompleted = globalIndex < maxIdx

                  return (
                    <StageCard
                      key={s.id}
                      id={s.id}
                      title={stageName(s)}
                      subtitle={`Capítulo ${s.chapter}`}
                      backgroundUrl={getStageBackground(s.id)}
                      status={
                        isLocked ? 'locked' : isCompleted ? 'completed' : 'available'
                      }
                      badge={isNext ? t('ui.next_badge') : undefined}
                      selected={isNext}
                      onSelect={(id) => navigate(`/play/${id}?index=${globalIndex}`)}
                    />
                  )
                })}
              </div>
            </section>
          ))}

          <section className="menu-section">
            <div className="menu-section__header">
              <h2 className="menu-section__title">{t('ui.special_challenges')}</h2>
              <button
                className="ranking-trigger-btn"
                onClick={() => navigate('/play/stage_00_tutorial')}
              >
                Tutorial
              </button>
              <button
                className="ranking-trigger-btn"
                onClick={() => setIsRankingOpen(true)}
              >
                {t('ui.global_ranking')}
              </button>
            </div>
            <div className="menu-section__grid">
              {SPECIALS.map((s) => (
                <StageCard
                  key={s.id}
                  id={s.id}
                  title={t(`specials.${s.id}_name`)}
                  subtitle={t(`specials.${s.id}_desc`)}
                  backgroundUrl={getStageBackground(s.id)}
                  status={
                    s.kind === 'boss'
                      ? 'special-boss'
                      : s.kind === 'training'
                        ? 'special-training'
                        : s.kind === 'survival'
                          ? 'special-survival'
                          : 'special-rhythm'
                  }
                  onSelect={(id) => navigate(`/play/${id}`)}
                />
              ))}
            </div>
          </section>

        </div>
      </main>

      <div style={{ display: 'none' }}>
        <SettingsIcon />
      </div>
    </div>
  )
}

function SettingsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"></circle>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
    </svg>
  )
}
